export type EmployerCalculatorInputs = {
  employees: number;
  salary: number;
  turnover: number;
  replace: number;
  sickdays: number;
  healthcare: number;
  presenteeism: number;
  vacancyweeks: number;
};

export type EmployerCalculatorResults = {
  prodLoss: number;
  turnCost: number;
  absCost: number;
  otherCost: number;
  total: number;
  costPerEmp: number;
  payrollPct: number;
  stressed: number;
  potentialSaving: number;
};

export const DEFAULT_INPUTS: EmployerCalculatorInputs = {
  employees: 1000,
  salary: 65470,
  turnover: 10,
  replace: 32735,
  sickdays: 3,
  healthcare: 700,
  presenteeism: 17,
  vacancyweeks: 10,
};

/** Formats a dollar amount as a full integer with comma separators (e.g. $65,470). */
export const formatDollar = (amount: number): string =>
  `$${Math.round(amount).toLocaleString("en-US")}`;

/** Abbreviates large dollar amounts to K/M suffixes for compact display (e.g. $2.1M). */
export const formatCompactDollar = (amount: number): string => {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (amount >= 1_000) return `$${Math.round(amount / 1_000)}K`;
  return `$${Math.round(amount)}`;
};

/** Formats a number as a percentage string with one decimal place (e.g. 7.4%). */
export const formatPercent = (value: number): string => `${value.toFixed(1)}%`;

/** Formats a number as a locale-aware integer string (e.g. 1,234). */
export const formatCount = (value: number): string => Math.round(value).toLocaleString("en-US");

/** Estimates the annual cost of employee financial stress across productivity loss, stress-induced turnover, absenteeism, and healthcare/vacancy. */
export const calcResults = (inputs: EmployerCalculatorInputs): EmployerCalculatorResults => {
  const { employees, salary, turnover, replace, sickdays, healthcare, presenteeism, vacancyweeks } = inputs;
  const turnoverRatio = turnover / 100;
  const presenteeismRatio = presenteeism / 100;
  const stressed = employees * 0.6;
  const activelyStressedEmployees = stressed * 0.57;
  const prodLoss = activelyStressedEmployees * (3 / 40) * salary + stressed * presenteeismRatio * salary * 0.3;
  const stressInducedDepartures = employees * turnoverRatio * 0.4;
  const turnCost = stressInducedDepartures * replace;
  const absCost = activelyStressedEmployees * sickdays * (salary / 260);
  const otherCost = stressed * healthcare + stressInducedDepartures * (vacancyweeks / 52) * salary * 0.5;
  const total = prodLoss + turnCost + absCost + otherCost;
  return {
    prodLoss,
    turnCost,
    absCost,
    otherCost,
    total,
    costPerEmp: employees > 0 ? total / employees : 0,
    payrollPct: employees > 0 && salary > 0 ? (total / (employees * salary)) * 100 : 0,
    stressed,
    potentialSaving: total * 0.3,
  };
};
